import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Twitter, Facebook, Linkedin, MessageCircle, Link as LinkIcon, Check } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";
import JsonLd from "@/components/JsonLd";

function ShareRow({ title, slug }) {
  const [copied, setCopied] = useState(false);
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  // Canonical URL for users (clean, shown in copy-link)
  const canonical = `${origin}/blog/${slug}`;
  // Social share URL routes through backend OG endpoint so crawlers
  // get rich preview meta tags; real browsers are redirected to the SPA.
  const shareUrl = `${origin}/api/og/blog/${slug}`;
  const text = `${title} — Inspire Da Streets · Nina Capone`;
  const enc = encodeURIComponent;

  const links = [
    {
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`,
      Icon: Twitter,
      testid: "share-twitter",
    },
    {
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      Icon: Facebook,
      testid: "share-facebook",
    },
    {
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
      Icon: Linkedin,
      testid: "share-linkedin",
    },
    {
      label: "Share on WhatsApp",
      href: `https://api.whatsapp.com/send?text=${enc(text + " " + shareUrl)}`,
      Icon: MessageCircle,
      testid: "share-whatsapp",
    },
  ];

  const copy = async () => {
    // Copy the OG-friendly URL so the link produces a rich preview wherever
    // it's pasted (Facebook, iMessage, WhatsApp, X, LinkedIn). The backend
    // OG endpoint auto-redirects real browsers to /blog/{slug}.
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid="blog-share-row"
    >
      <span className="text-[10px] uppercase tracking-[0.3em] text-white/45 shrink-0">
        Share This Story
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {links.map(({ label, href, Icon, testid }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={label}
            data-testid={testid}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-white/70 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]/50 transition-all duration-200"
          >
            <Icon size={16} strokeWidth={1.5} />
          </a>
        ))}
        <button
          type="button"
          onClick={copy}
          aria-label="Copy share link"
          title="Copy a share-friendly link with rich preview"
          data-testid="share-copy"
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-full border text-[11px] uppercase tracking-[0.2em] transition-all duration-200 ${
            copied
              ? "border-[color:var(--gold)]/60 text-[color:var(--gold)]"
              : "border-white/10 text-white/70 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]/50"
          }`}
        >
          {copied ? <Check size={14} /> : <LinkIcon size={14} />}
          {copied ? "Copied · Ready to Share" : "Copy Share Link"}
        </button>
      </div>
    </div>
  );
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);
  const [jsonLd, setJsonLd] = useState(null);

  useEffect(() => {
    api
      .get(`/public/blog/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setError("Article not found"));
    api
      .get(`/seo/structured/blog/${slug}`)
      .then((r) => setJsonLd(r.data))
      .catch(() => setJsonLd(null));
  }, [slug]);

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="font-serif text-3xl">{error}</p>
        <Link
          to="/inspire"
          className="link-gold inline-flex items-center gap-2 mt-6"
        >
          <ArrowLeft size={14} /> Back to Wellness Blog
        </Link>
      </div>
    );

  if (!article)
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-white/40 font-serif italic">
        Loading…
      </div>
    );

  return (
    <article
      className="max-w-3xl mx-auto px-6 py-20 lg:py-32"
      data-testid="blog-article"
    >
      {jsonLd && <JsonLd data={jsonLd} id="blog-jsonld" />}
      <Link
        to="/inspire"
        className="link-gold inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-12"
      >
        <ArrowLeft size={14} /> Wellness Blog
      </Link>
      <SectionLabel label={article.category} />
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-light leading-[1] mb-6">
        {article.title}
      </h1>
      <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-12">
        By {article.author} ·{" "}
        {new Date(article.created_at).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
        {article.ai_generated && (
          <span
            className="ml-3 inline-flex items-center gap-1.5 px-2 py-1 border text-[9px]"
            style={{
              color: "var(--gold)",
              borderColor: "rgba(232,180,184,0.4)",
            }}
            data-testid="ai-editorial-badge"
          >
            <span
              className="inline-block w-1 h-1 rounded-full"
              style={{ background: "var(--gold)" }}
            />
            Weekly · Editorial Assist
          </span>
        )}
      </p>
      <img
        src={imgUrl(article.cover_image) || "https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"}
        alt={article.title}
        className="w-full aspect-[16/9] object-cover mb-12"
        loading="lazy"
        onError={(e) => {
          const fb = "https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
          if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
        }}
        data-testid="article-cover"
      />
      <div className="font-serif text-lg sm:text-xl lg:text-2xl text-white/85 leading-[1.55] space-y-6">
        {article.content.split("\n\n").map((p, i) => (
          <p key={`${article.slug || slug}-p-${i}`}>{p}</p>
        ))}
      </div>
      <ShareRow title={article.title} slug={article.slug || slug} />
      {article.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-16 pt-10 border-t border-white/5">
          {article.tags.map((t) => (
            <span
              key={t}
              className="text-[10px] uppercase tracking-[0.25em] px-3 py-1 border border-white/10 text-white/55"
            >
              #{t}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
